/* SettingsPopUp.tsx ------------------------------------------------------- */
/* Full-sized settings screen (front/back pop-ups share the same width).    */
/* ------------------------------------------------------------------------ */
import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  IconButton,
  FormControlLabel,
  Switch,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SettingsIcon from "@mui/icons-material/Settings";

/* cloud-function helpers -------------------------------------------------- */
import {
  updatePreferences,
  type NotificationPrefs,
} from "../../../api/wallet/updatePreferences";

import { StoreCard } from "../../../lib/types/detailsTypes";
import { deleteCard } from "../../../api/wallet/deleteCard";

/* ------------------------------------------------------------------------ */
/*  Types                                                                   */
/* ------------------------------------------------------------------------ */
interface SettingsPopUpProps {
  card: StoreCard;
  onBack: () => void;
}

/* ------------------------------------------------------------------------ */
/*  Component                                                               */
/* ------------------------------------------------------------------------ */
const SettingsPopUp: React.FC<SettingsPopUpProps> = ({ card, onBack }) => {
  /* ---- initialise local state ---------------------------------------- */
  const [prefs, setPrefs] = useState<NotificationPrefs>({
    emailEnabled: card.notificationPrefs?.emailEnabled ?? false,
    pushEnabled: card.notificationPrefs?.pushEnabled ?? true,
    smsEnabled: card.notificationPrefs?.smsEnabled ?? false,
  });

  const [editing, setEditing] = useState(false); // “edit” vs “update” mode
  const [saving, setSaving] = useState(false); // show loading state
  const [confirmOpen, setConfirmOpen] = useState(false); // delete dialog

  /* ---- handlers ------------------------------------------------------- */
  const toggleField = (field: keyof NotificationPrefs) => () =>
    setPrefs((prev) => ({ ...prev, [field]: !prev[field] }));

  const handleSave = async () => {
    setSaving(true);
    await updatePreferences(card.storeID, card.id, prefs);
    setSaving(false);
    setEditing(false);
  };

  const handleDelete = async () => {
    setConfirmOpen(false);
    try {
      await deleteCard({
        storeID: card.storeID,
        cardDocId: card.id,
      });
      onBack();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  /* -------------------------------------------------------------------- */
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f9f9f9",
        borderRadius: 2,
        boxShadow: "0 0 10px rgba(0 0 0 / 10%)",
        width: "75vw",
        maxWidth: 500,
        height: "70vh",
        p: 2,
      }}
    >
      {/* ─── HEADER (back button now vertically centred) ─── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="left"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <IconButton aria-label="Back" onClick={onBack} edge="start">
          <ArrowBackIcon />
        </IconButton>
        <SettingsIcon color="action" />
        <Typography variant="h5">Card&nbsp;Settings</Typography>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* ──────────────────────────────────────────────────────────────── */}
      {/*  MARKETING / NOTIFICATION PREFERENCES                           */}
      {/* ──────────────────────────────────────────────────────────────── */}
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Marketing Preferences&nbsp;
        <Typography component="span" variant="caption">
          (store-wide)
        </Typography>
      </Typography>

      <Stack sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              disabled={!editing}
              checked={prefs.emailEnabled}
              onChange={toggleField("emailEnabled")}
            />
          }
          label="E-mail notifications"
        />
        <FormControlLabel
          control={
            <Switch
              disabled={!editing}
              checked={prefs.pushEnabled}
              onChange={toggleField("pushEnabled")}
            />
          }
          label="Push notifications"
        />
        <FormControlLabel
          control={
            <Switch
              disabled={!editing}
              checked={prefs.smsEnabled}
              onChange={toggleField("smsEnabled")}
            />
          }
          label="SMS notifications"
        />
      </Stack>

      {/* ─── ACTION BUTTONS ─── */}
      <Stack mt="auto" spacing={1}>
        {!editing ? (
          /* START EDIT MODE */
          <Button
            variant="contained"
            onClick={() => setEditing(true)}
            fullWidth
          >
            Edit Preferences
          </Button>
        ) : (
          /* SAVE CHANGES */
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            fullWidth
          >
            {saving ? "Saving…" : "Update Preferences"}
          </Button>
        )}

        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteForeverIcon />}
          onClick={() => setConfirmOpen(true)}
          fullWidth
        >
          Delete Card
        </Button>
      </Stack>

      {/* ──────────────────────────────────────────────────────────────── */}
      {/*  DELETE CONFIRMATION                                             */}
      {/* ──────────────────────────────────────────────────────────────── */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="confirm-delete-card"
      >
        <DialogTitle id="confirm-delete-card">Delete Loyalty Card?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently remove the card and&nbsp;
            <strong>cannot</strong> be undone. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPopUp;
